import resize from "@jsquash/resize";
import { encode } from "@jsquash/webp";

const DEFAULT_THUMBNAIL_WIDTH = 640;
const DEFAULT_THUMBNAIL_QUALITY = 0.5;
const DEFAULT_DISPLAY_WIDTH = 1284;
const DEFAULT_DISPLAY_QUALITY = 0.8;

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const binaryString = bytes.reduce(
    (acc, byte) => acc + String.fromCharCode(byte),
    "",
  );
  return btoa(binaryString);
}

function validateParameters(maxWidth: number, quality: number): void {
  if (maxWidth <= 0) {
    throw new Error("maxWidth must be a positive number");
  }
  if (quality < 0 || quality > 1) {
    throw new Error("quality must be between 0 and 1");
  }
}

async function getImageData(source: string | File): Promise<ImageData> {
  const img = new Image();
  const objectUrl =
    source instanceof File ? URL.createObjectURL(source) : source;

  try {
    if (typeof source === "string" && !source.startsWith("blob:")) {
      img.crossOrigin = "anonymous";
    }

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = objectUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Failed to get canvas 2D context");
    }

    ctx.drawImage(img, 0, 0);
    return ctx.getImageData(0, 0, img.width, img.height);
  } finally {
    if (source instanceof File) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

async function optimizeToBase64Internal(
  source: string | File,
  maxWidth: number,
  quality: number,
): Promise<string> {
  validateParameters(maxWidth, quality);

  const imageData = await getImageData(source);
  const { width: originalWidth, height: originalHeight } = imageData;

  const shouldResize = originalWidth > maxWidth;
  const resizedData = shouldResize
    ? await resize(imageData, {
        width: maxWidth,
        height: Math.round((originalHeight / originalWidth) * maxWidth),
      })
    : imageData;

  const webpBuffer = await encode(resizedData, {
    quality: quality * 100,
  });

  return arrayBufferToBase64(webpBuffer);
}

export async function optimizeImageToBase64(
  imageUrl: string,
  maxWidth = DEFAULT_THUMBNAIL_WIDTH,
  quality = DEFAULT_THUMBNAIL_QUALITY,
): Promise<string> {
  return optimizeToBase64Internal(imageUrl, maxWidth, quality);
}

export async function optimizeFileToBase64(
  file: File,
  maxWidth = DEFAULT_THUMBNAIL_WIDTH,
  quality = DEFAULT_THUMBNAIL_QUALITY,
): Promise<string> {
  return optimizeToBase64Internal(file, maxWidth, quality);
}

export async function imageUrlToBase64(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.statusText}`);
  }

  const blob = await response.blob();
  const arrayBuffer = await blob.arrayBuffer();

  return arrayBufferToBase64(arrayBuffer);
}

export async function optimizeImageForDisplay(
  file: File,
  maxWidth = DEFAULT_DISPLAY_WIDTH,
  quality = DEFAULT_DISPLAY_QUALITY,
): Promise<Blob> {
  validateParameters(maxWidth, quality);

  const imageData = await getImageData(file);
  const { width: originalWidth, height: originalHeight } = imageData;

  const shouldResize = originalWidth > maxWidth;
  const resizedData = shouldResize
    ? await resize(imageData, {
        width: maxWidth,
        height: Math.round((originalHeight / originalWidth) * maxWidth),
      })
    : imageData;

  const webpBuffer = await encode(resizedData, {
    quality: quality * 100,
  });

  return new Blob([webpBuffer], { type: "image/webp" });
}
